import { selectDepartments } from "app/containers/AdminConsole/slice";
import Button from "app/storybookComponents/Button";
import Select, { GroupBase } from "react-select";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import NoResultsIcon from "resources/images/no-results-transparent-back.png";
import { selectAllUserSubordinates } from "app/containers/UserSettings/slice";
import { useAppSelector } from "utils/redux/hooks";
import { ManagerCustomListItem } from "./types";
import { Collapse } from "react-bootstrap";
import { getS, getSelectProps, getUserName } from "utils/helperFunctions";
import { useCallback, useMemo, useState } from "react";
import CardWithPopoverUsers from "app/components/Popovers/CardWithPopoverUsers";
import AvatarCircle from "app/components/AvatarCircle";
import { getMemberOption } from "app/components/GlobalNavbar/helpers";
import { getDepartmentLeadId } from "../AdminConsole/helpers";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "./constants";

const MAX_ENTITIES_SHOWN_WHILE_COLLAPSED = 5;

interface Props {
  teamIds?: number[];
  departmentIds?: number[];
  managers?: ManagerCustomListItem[];
  emptyState?:
    | JSX.Element
    | {
        title: string;
        description: string;
      }
    | null;
  onTeamRemove?: (teamId: number) => void;
  onManagerRemove?: (manager: ManagerCustomListItem) => void;
  onDepartmentRemove?: (departmentId: number) => void;
}

interface Option {
  label: string;
  value: string;
  entityType: string;
}

const CustomListSearchComponent = ({
  teamIds,
  departmentIds,
  managers,
  emptyState,
  onDepartmentRemove,
  onManagerRemove,
  onTeamRemove,
}: Readonly<Props>) => {
  // ------------------ Selectors ------------------
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const departmentsById = useAppSelector(selectDepartments);
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);

  // ------------------ States ------------------
  const [entitySelected, setEntitySelected] = useState<null | Option>(null);
  const [collapseState, setCollapseState] = useState<Record<string, boolean>>(
    {}
  );

  // ------------------ Callbacks/Memo ------------------
  const formatOptionLabel = useCallback(
    (option: Option) => {
      switch (option.entityType) {
        case "user": {
          const avatarCircle = (
            <AvatarCircle
              name={`${option.label}`}
              userAccountId={Number(option.value)}
              size="small"
            />
          );
          return getMemberOption({
            label: option.label,
            avatarCircle,
          });
        }
        case "team": {
          const teamId = Number(option.value);
          const team = teamsById[teamId];
          const avatarCircle = (
            <AvatarCircle
              name={`${option.label}`}
              size="small"
              profilePicture={team?.profilePicture}
              avatarColor={team?.avatarColor}
            />
          );
          return getMemberOption({
            label: option.label,
            avatarCircle,
          });
        }
        case "department": {
          const avatarCircle = (
            <AvatarCircle name={`${option.label}`} size="small" />
          );
          return getMemberOption({
            label: option.label,
            avatarCircle,
          });
        }
        default:
          return option.label;
      }
    },
    [teamsById]
  );

  // Get user option for selected value
  const getUserSelectedOption = useCallback(
    (userAccountId: number) => {
      const user = usersById[userAccountId];
      return user
        ? {
            value: `${userAccountId}`,
            label: `${user.firstName} ${user.lastName}`,
            entityType: "user",
            data: userAccountId,
          }
        : null;
    },
    [usersById]
  );

  // Get team option for selected value
  const getTeamSelectedOption = useCallback(
    (id: number) => {
      const team = teamsById[id];
      return team
        ? {
            value: `team-${id}`,
            label: team.teamName,
            entityType: "team",
            data: id,
          }
        : null;
    },
    [teamsById]
  );

  // Get department option for selected value
  const getDepartmentSelectedOption = useCallback(
    (id: number) => {
      const department = departmentsById[id];
      return department
        ? {
            value: `department-${id}`,
            label: department.name ?? "",
            entityType: "department",
            data: id,
          }
        : null;
    },
    [departmentsById]
  );

  const userOptions = useMemo(() => {
    const allUserAccountIds: number[] = [];
    managers?.forEach((manager) => {
      const subordinates = allUsersSubordinates[manager.userAccountId];
      if (manager.entireReportingStructure) {
        allUserAccountIds.push(...(subordinates?.allSubordinates ?? []));
      } else {
        allUserAccountIds.push(...(subordinates?.directReports ?? []));
      }
    });

    teamIds?.forEach((teamId) => {
      const team = teamsById[teamId];
      if (!team?.teamMemberIds) return;
      allUserAccountIds.push(...team.teamMemberIds);
    });

    departmentIds?.forEach((departmentId) => {
      const department = departmentsById[departmentId];
      if (!department?.teamMembers) return;
      allUserAccountIds.push(...department.teamMembers);
    });

    const uniqueUserIds = [...new Set(allUserAccountIds)];

    return uniqueUserIds
      .map((userAccountId) => {
        const user = usersById[userAccountId];
        return {
          value: `${userAccountId}`,
          label: getUserName(user),
          entityType: "user",
          data: userAccountId,
        };
      })
      .filter(Boolean);
  }, [
    managers,
    usersById,
    teamIds,
    teamsById,
    departmentIds,
    departmentsById,
    allUsersSubordinates,
  ]);

  const teamOptions = useMemo(() => {
    const allTeamIds: number[] = [];
    allTeamIds.push(...(teamIds || []));
    departmentIds?.forEach((departmentId) => {
      const department = departmentsById[departmentId];
      if (!department?.teams) return;
      allTeamIds.push(...department.teams);
    });
    const uniqueTeamIds = [...new Set(allTeamIds)];
    return uniqueTeamIds
      .map((teamId) => {
        const team = teamsById[teamId];
        return {
          value: `${teamId}`,
          label: team?.teamName,
          entityType: "team",
          data: teamId,
        };
      })
      .filter(Boolean);
  }, [teamIds, teamsById, departmentIds, departmentsById]);

  const departmentOptions = useMemo(() => {
    return (departmentIds || [])
      .map((departmentId) => {
        const department = departmentsById[departmentId];
        return {
          value: `${departmentId}`,
          label: department?.name ?? "",
          entityType: "department",
          data: departmentId,
        };
      })
      .filter(Boolean);
  }, [departmentIds, departmentsById]);

  const groupedOptions = useMemo(() => {
    const result = [];

    if (userOptions.length > 0) {
      result.push({
        label: "Users added",
        options: userOptions,
      });
    }

    if (teamOptions.length > 0) {
      result.push({
        label: "Teams added",
        options: teamOptions,
      });
    }

    if (departmentOptions.length > 0) {
      result.push({
        label: "Departments added",
        options: departmentOptions,
      });
    }

    return result;
  }, [userOptions, teamOptions, departmentOptions]);

  const filteredManagers = useMemo(() => {
    if (!entitySelected) {
      return managers;
    }

    if (entitySelected.entityType !== "user") {
      return [];
    }

    const foundManagers = managers?.filter((manager) => {
      const selectedUserAccountId = Number(entitySelected.value);
      if (selectedUserAccountId === manager.userAccountId) {
        return true;
      }
      const managersSubordinates = allUsersSubordinates[manager.userAccountId];
      if (manager.entireReportingStructure) {
        return managersSubordinates?.allSubordinates?.includes(
          selectedUserAccountId
        );
      }
      return managersSubordinates?.directReports?.includes(
        selectedUserAccountId
      );
    });
    return foundManagers ?? [];
  }, [managers, entitySelected, allUsersSubordinates]);

  const filteredTeamIds = useMemo(() => {
    if (!entitySelected) {
      return teamIds;
    }
    if (entitySelected.entityType === "department") {
      return [];
    }
    const entityId = Number(entitySelected.value);

    if (entitySelected.entityType === "team") {
      const foundTeam = teamIds?.find((teamId) => teamId === entityId);
      return foundTeam ? [foundTeam] : [];
    }

    const teamIdsWithMemberIn = teamIds?.filter((teamId) => {
      const team = teamsById[teamId];
      return team?.teamMemberIds?.includes(entityId);
    });

    return teamIdsWithMemberIn;
  }, [teamIds, entitySelected, teamsById]);

  const filteredDepartmentIds = useMemo(() => {
    if (!entitySelected) {
      return departmentIds;
    }
    const entityId = Number(entitySelected.value);

    if (entitySelected.entityType === "department") {
      const foundDepartment = departmentIds?.find(
        (departmentId) => departmentId === entityId
      );
      return foundDepartment ? [foundDepartment] : [];
    }

    if (entitySelected.entityType === "team") {
      const team = teamsById[entityId];
      const departmentId = team?.departmentId;
      return departmentId ? [departmentId] : [];
    }

    const departmentIdsWithMemberIn = departmentIds?.filter((departmentId) => {
      const department = departmentsById[departmentId];
      return department?.teamMembers?.includes(entityId);
    });

    return departmentIdsWithMemberIn;
  }, [departmentIds, entitySelected, departmentsById, teamsById]);

  // ----------------- Render Helpers -----------------

  const getManagerDescription = (manager: ManagerCustomListItem) => {
    const subordinates = allUsersSubordinates[manager.userAccountId];
    if (manager.entireReportingStructure) {
      return `and their reporting structure (${
        subordinates?.allSubordinates.length ?? "..."
      })`;
    }

    return `and their direct reports (${
      subordinates?.directReports.length ?? "..."
    })`;
  };

  const renderSelectedEntitySection = (
    title: string,
    elements: (JSX.Element | null)[],
    collapseKey: string
  ) => {
    const initialElements = elements.slice(
      0,
      MAX_ENTITIES_SHOWN_WHILE_COLLAPSED
    );
    const remainingElements = elements.slice(
      MAX_ENTITIES_SHOWN_WHILE_COLLAPSED
    );
    const isCollapsed = collapseKey ? collapseState[collapseKey] : false;

    return (
      <div className="column-gap-12px">
        <div className="check-in-custom-list-selected-entities-title">
          <p>{title}</p>
        </div>

        <div className="selected-items-container">{initialElements}</div>
        {remainingElements.length ? (
          <>
            <Collapse in={!!isCollapsed}>
              <div className="selected-items-container">
                {remainingElements}
              </div>
            </Collapse>
            <Button
              variant="link"
              onClick={() =>
                setCollapseState({
                  ...collapseState,
                  [collapseKey]: !isCollapsed,
                })
              }
            >
              {isCollapsed ? "Collapse" : `See all ${collapseKey}`}
            </Button>
          </>
        ) : null}
      </div>
    );
  };

  const getCardWithPopoverEmptyState = useCallback(
    (title: string, description: string) => {
      return (
        <div className="line-chart-empty-state">
          <div>
            <div className="line-chart-empty-state__title">
              <p>{title}</p>
            </div>
            <div className="line-chart-empty-state__description">
              <p>{description}</p>
            </div>
          </div>
        </div>
      );
    },
    []
  );

  const getManagerUserAccountIds = (manager: ManagerCustomListItem) => {
    const subordinates = allUsersSubordinates[manager.userAccountId];
    if (manager.entireReportingStructure) {
      return subordinates?.allSubordinates ?? [];
    }
    return subordinates?.directReports ?? [];
  };

  const getManagerSelectedOptions = () => {
    if (!filteredManagers?.length) {
      return null;
    }

    const managerElms = filteredManagers.map((manager) => {
      const user = usersById[manager.userAccountId];
      if (!user) return null;
      const userName = `${user.firstName} ${user.lastName}`.trim();
      const name = userName || user.emailAddress;
      return (
        <CardWithPopoverUsers
          title={name}
          description={getManagerDescription(manager)}
          userAccountIds={getManagerUserAccountIds(manager)}
          onRemove={() => onManagerRemove?.(manager)}
          key={manager.userAccountId}
          emptyState={getCardWithPopoverEmptyState(
            "No direct reports",
            `Currently, there are no direct reports for ${
              name ?? "this manager"
            }`
          )}
        />
      );
    });
    return renderSelectedEntitySection("Managers", managerElms, "managers");
  };

  const getTeamSelectedOptions = () => {
    if (!filteredTeamIds?.length) {
      return null;
    }

    const teams = filteredTeamIds.map((teamId) => {
      const team = teamsById[teamId];
      if (!team) return null;
      const userTagsById: Record<number, string> = {};
      team.teamLeadUserAccountIds?.forEach((userAccountId) => {
        userTagsById[userAccountId] = "Team Lead";
      });

      const totalMembers = team.teamMemberIds?.length ?? 0;
      const departmentName = team.departmentId
        ? departmentsById[team.departmentId]?.name
        : "";
      return (
        <CardWithPopoverUsers
          title={team.teamName}
          description={`${totalMembers} member${getS(totalMembers)}`}
          userAccountIds={team.teamMemberIds ?? []}
          onRemove={() => onTeamRemove?.(teamId)}
          additionalInfo={departmentName}
          userTagsById={userTagsById}
          key={teamId}
          emptyState={getCardWithPopoverEmptyState(
            "No team members",
            `Currently, there are no team members in ${team.teamName}`
          )}
        />
      );
    });
    return renderSelectedEntitySection("Teams", teams, "teams");
  };

  const getDepartmentSelectedOptions = () => {
    if (!filteredDepartmentIds?.length) {
      return null;
    }

    const departments = filteredDepartmentIds.map((departmentId) => {
      const department = departmentsById[departmentId];
      if (!department) return null;
      const userTagsById: Record<number, string> = {};
      const departmentLeaderId = getDepartmentLeadId(
        departmentId,
        departmentsById
      );
      if (departmentLeaderId) {
        userTagsById[departmentLeaderId] = "Department Lead";
      }

      const totalMembers = department.teamMembers?.length ?? 0;
      return (
        <CardWithPopoverUsers
          title={department.name ?? ""}
          description={`${totalMembers} member${getS(totalMembers)}`}
          userAccountIds={department.teamMembers ?? []}
          onRemove={() => onDepartmentRemove?.(departmentId)}
          userTagsById={userTagsById}
          key={departmentId}
          emptyState={getCardWithPopoverEmptyState(
            "No department members",
            `Currently, there are no department members in ${
              department.name ?? "this department"
            }`
          )}
        />
      );
    });
    return renderSelectedEntitySection(
      "Departments",
      departments,
      "departments"
    );
  };

  const getEmptyState = () => {
    if (!emptyState) {
      return (
        <div className="empty-card" style={{ padding: "40px" }}>
          <img
            src={NoResultsIcon}
            alt="no results"
            className="line-chart-empty-state__illustration"
            style={{ marginBottom: "40px" }}
          />
          <span style={{ marginBottom: "8px" }}>
            No one with {WEEKLY_CHECK_IN_FEATURE_NAME}s enabled
          </span>
          <p>
            Teams and managers you enable {WEEKLY_CHECK_IN_FEATURE_NAME}s for
            will be shown here.
          </p>
          {/* <p>Departments, teams, and managers you enable {WEEKLY_CHECK_IN_FEATURE_NAME}s for will be shown here.</p> */}
        </div>
      );
    }

    if ("title" in emptyState) {
      return (
        <div className="empty-card" style={{ padding: "40px" }}>
          <img
            src={NoResultsIcon}
            alt="no results"
            className="line-chart-empty-state__illustration"
            style={{ marginBottom: "40px" }}
          />
          <span style={{ marginBottom: "8px" }}>{emptyState.title}</span>
          <p>{emptyState.description}</p>
        </div>
      );
    }

    return emptyState;
  };

  // Format group header with count
  const formatGroupHeader = (data: GroupBase<Option>) => (
    <div className="custom-group-heading">
      <span>{data.label ?? ""}</span>
    </div>
  );

  // Handle selection change
  const handleSelectionChange = (selectedOption: Option | null) => {
    if (!selectedOption) {
      setEntitySelected(null);
      return;
    }

    setEntitySelected(selectedOption);
  };

  const getSelectedValue = () => {
    if (!entitySelected) return null;

    const { value, entityType } = entitySelected;
    const id = Number(value);

    switch (entityType) {
      case "user":
        return getUserSelectedOption(id);
      case "team":
        return getTeamSelectedOption(id);
      case "department":
        return getDepartmentSelectedOption(id);
      default:
        return null;
    }
  };

  const getSelectedValueText = () => {
    if (!entitySelected) return null;

    const { value, entityType } = entitySelected;
    const id = Number(value);

    switch (entityType) {
      case "user": {
        const user = usersById[id];
        if (!user) return null;
        const name = `${user.firstName} ${user.lastName}`;
        return `${name} is included in:`;
      }
      case "team": {
        const team = teamsById[id];
        if (!team) return null;
        return `${team.teamName} is included in:`;
      }
      case "department": {
        const department = departmentsById[id];
        if (!department) return null;
        return `${department.name} is included in:`;
      }
      default:
        return null;
    }
  };

  const getNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if (inputValue) {
      return (
        <div className="line-chart-empty-state">
          <div>
            <div className="line-chart-empty-state__title">
              <p>No search results found</p>
            </div>
            <div className="line-chart-empty-state__description">
              <p>
                No results matching "{inputValue}" found. Please try a different
                name.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!managers?.length && !teamIds?.length && !departmentIds?.length) {
    return getEmptyState();
  }
  const { selectStyles, components } = getSelectProps("search");
  const selectedValueText = getSelectedValueText();

  return (
    <>
      <div className="entity-search" style={{ marginTop: "8px" }}>
        <div className="search-input-container">
          <Select<Option>
            noOptionsMessage={getNoOptionsMessage}
            placeholder="Search by name..."
            options={groupedOptions}
            styles={selectStyles}
            components={components}
            value={getSelectedValue()}
            onChange={handleSelectionChange}
            formatGroupLabel={formatGroupHeader}
            isClearable
            formatOptionLabel={formatOptionLabel}
            classNames={{
              option: () => "option-value-with-border-bottom option",
            }}
          />
        </div>
      </div>

      {selectedValueText && (
        <p className="sapphire-150-text fw-bold">{selectedValueText}</p>
      )}

      <div className="column-gap-24px">
        {getManagerSelectedOptions()}
        {getTeamSelectedOptions()}
        {getDepartmentSelectedOptions()}
      </div>
    </>
  );
};

export default CustomListSearchComponent;
